import { useMemo } from "react";
import { Head, router, useForm } from "@inertiajs/react";
import { ExternalLink, Gift, Goal, Link2, Star, Trophy, Tv } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import TvSlideshow from "@/components/tv/TvSlideshow";
import TvStage from "@/components/tv/TvStage";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useConfirm } from "@/components/shared/ConfirmProvider";
import { cn } from "@/lib/utils";
import { formatTvCode } from "@/lib/tvDisplay";

// What each part of the display is, in slide order. Keys mirror
// School::TV_DISPLAY_PARTS.
const PARTS = [
  { key: "goal", icon: Goal, name: "The goal", detail: "Money raised and minutes read against your goals, and the next school reward." },
  { key: "rewards", icon: Gift, name: "School rewards", detail: "Every reward the school earns together, and how close each one is." },
  { key: "readers", icon: Trophy, name: "Top readers", detail: "The 10 students who've read the most, by first name and last initial." },
  { key: "classes", icon: Star, name: "Top reader in every class", detail: "Each class's total minutes and its top reader." },
  { key: "donate_link", icon: Link2, name: "Donation link", detail: "Your school's donation page address along the bottom of the screen." },
];

/**
 * The TV display page for school admins (BUSINESS_RULES §9): choose what
 * the hallway display shows, with a live preview of the real slideshow,
 * and the code for putting it on a TV.
 */
export default function TvDisplay({ school, membership, parts, preview, code, url, code_screen_url, update_path, rotate_path }) {
  const form = useForm({ enabled: PARTS.map((p) => p.key).filter((key) => parts[key]) });
  const on = useMemo(
    () => Object.fromEntries(PARTS.map(({ key }) => [key, form.data.enabled.includes(key)])),
    [form.data.enabled],
  );
  // Kept in PARTS order, so switching something off and back on leaves
  // the form unchanged (not "dirty").
  const toggle = (key) =>
    form.setData("enabled", PARTS.map((p) => p.key).filter((k) => (k === key ? !on[k] : on[k])));

  const save = (e) => {
    e.preventDefault();
    form.patch(update_path, { preserveScroll: true });
  };

  return (
    <SchoolLayout school={school} membership={membership} title="TV display">
      <Head title="TV display" />

      <div className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>
              What the TV shows with the choices below. It moves to the next slide every 12–15 seconds; click the bars along the bottom to jump.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TvStage fit="container">
              <TvSlideshow
                content={{ ...preview, parts: on, donate_url: on.donate_link ? preview.donate_url : null }}
                interactive
              />
            </TvStage>
          </CardContent>
        </Card>

        <div className="grid gap-8 lg:grid-cols-[3fr_2fr]">
          <Card>
            <CardHeader>
              <CardTitle>What it shows</CardTitle>
              <CardDescription>TVs already showing the display pick up changes within a minute.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={save} className="space-y-5">
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {PARTS.map(({ key, icon: Icon, name, detail }) => (
                    <li key={key} className="flex items-start gap-4 px-4 py-3">
                      <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", on[key] ? "text-primary" : "text-muted-foreground/50")} />
                      <div className="min-w-0 flex-1">
                        <p className={cn("text-sm font-medium", on[key] ? "text-foreground" : "text-muted-foreground")}>{name}</p>
                        <p className="text-xs leading-relaxed text-muted-foreground">{detail}</p>
                      </div>
                      <Switch className="mt-0.5" checked={on[key]} onChange={() => toggle(key)} label={name} />
                    </li>
                  ))}
                </ul>
                {form.errors.base && <p className="text-sm text-destructive">{form.errors.base}</p>}
                <div className="flex flex-wrap items-center gap-3">
                  <Button type="submit" disabled={form.processing || !form.isDirty}>
                    Save
                  </Button>
                  {form.isDirty && <p className="text-sm text-muted-foreground">The preview shows your changes; TVs get them once you save.</p>}
                </div>
              </form>
            </CardContent>
          </Card>

          <TvCodeCard code={code} url={url} codeScreenUrl={code_screen_url} rotatePath={rotate_path} />
        </div>
      </div>
    </SchoolLayout>
  );
}

// The code for putting the display on a TV, reached only by code so a
// school's name alone doesn't find it.
function TvCodeCard({ code, url, codeScreenUrl, rotatePath }) {
  const confirm = useConfirm();

  const rotate = async () => {
    const ok = await confirm({
      title: "Get a new TV display code?",
      body: "The current code stops working right away. Any TV showing it goes back to the code screen until you enter the new one.",
      confirmLabel: "Get a new code",
    });
    if (ok) router.patch(rotatePath, {}, { preserveScroll: true });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tv className="h-5 w-5 text-primary" />
          Put it on a TV
        </CardTitle>
        <CardDescription>Works in any TV, laptop or projector browser.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm text-muted-foreground">
            On the TV, go to <span className="font-medium text-foreground">{codeScreenUrl.replace(/^https?:\/\//, "")}</span> and enter
          </p>
          <p className="mt-1 font-mono text-3xl font-semibold tracking-[0.2em] text-foreground font-tabular">{formatTvCode(code)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <a href={url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
              Open TV display
            </a>
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={rotate}>
            Get a new code
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Anyone with the code can see the display, so share it only with staff who set up screens.
        </p>
      </CardContent>
    </Card>
  );
}
